import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, errMsg, fmtNum } from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Plus,
  Save,
  Trash2,
  History,
  Lock,
  Pencil,
  X,
  GitBranch,
  Layers,
  Award,
  Settings2,
  Calculator,
  Coins,
  Clock,
  ArrowRight,
  Minus,
  TrendingUp,
  TrendingDown,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  QualityBadge,
  QUALITAETSKLASSE_ICONS,
  QUALITAETSKLASSE_FARBEN,
} from "@/components/app/QualityBadge";

// Kompakter Picker für Lucide-Icons zur Auswahl in Klassen-Editor.
// Zeigt das gewählte Icon inline im Trigger und listet die kuratierten
// Optionen aus QUALITAETSKLASSE_ICONS im Dropdown auf.
const IconPicker = ({ value, onChange, disabled, testId }) => {
  const Selected = QUALITAETSKLASSE_ICONS[value] || QUALITAETSKLASSE_ICONS.BadgeCheck;
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId} className="h-9">
        <div className="flex items-center gap-1.5">
          <Selected className="h-4 w-4" />
          <span className="text-xs">{value}</span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(QUALITAETSKLASSE_ICONS).map(([key, Icon]) => (
          <SelectItem key={key} value={key}>
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <span className="text-xs">{key}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Farb-Picker mit visuellem Farbmuster – bindet die semantischen Theme-Tokens
// success/info/warning/… an, damit Klassen unabhängig vom Light-/Dark-Mode
// konsistent gerendert werden.
const ColorPicker = ({ value, onChange, disabled, testId }) => {
  const cfg = QUALITAETSKLASSE_FARBEN[value] || QUALITAETSKLASSE_FARBEN.info;
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId} className="h-9">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-3.5 w-3.5 rounded-sm border"
            style={{ background: cfg.swatch }}
          />
          <span className="text-xs">{cfg.label}</span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(QUALITAETSKLASSE_FARBEN).map(([key, c]) => (
          <SelectItem key={key} value={key}>
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-3.5 w-3.5 rounded-sm border"
                style={{ background: c.swatch }}
              />
              <span className="text-xs">{c.label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const FELDER = [
  { key: "menge_kg", label: "Monatsmenge (kg)", typ: "number" },
  { key: "fett_prozent", label: "Fett %", typ: "number" },
  { key: "eiweiss_prozent", label: "Eiweiß %", typ: "number" },
  { key: "zellzahl_geo", label: "Zellzahl (geom. Mittel)", typ: "number" },
  { key: "keimzahl_geo", label: "Keimzahl (geom. Mittel)", typ: "number" },
  { key: "hemmstoff_positiv", label: "Hemmstoff positiv", typ: "boolean" },
  { key: "qualitaetsklasse", label: "Qualitätsklasse", typ: "text" },
  { key: "gvo_frei", label: "GVO-frei (Milchsorte)", typ: "boolean" },
];
const OPERATOREN = [
  { key: ">=", label: "≥" },
  { key: ">", label: ">" },
  { key: "<=", label: "≤" },
  { key: "<", label: "<" },
  { key: "==", label: "=" },
  { key: "!=", label: "≠" },
];

const morgen = () =>
  new Date(Date.now() + 86400000).toISOString().slice(0, 10);

const leereKlasse = () => ({
  name: "Neu",
  beschreibung: "",
  prioritaet: 99,
  zellzahl_max: 400000,
  keimzahl_max: 100000,
  cent_je_kg: 0,
  icon: "BadgeCheck",
  farbe: "info",
});
const leereStaffel = () => ({
  ab_menge_kg: 10000,
  cent_je_kg: 0.5,
  beschreibung: "",
});
const leereRegel = () => ({
  name: "Neue Regel",
  beschreibung: "",
  cent_je_kg: 0,
  bedingungen: [],
  verknuepfung: "und",
  aktiv: true,
});
const leereRevision = () => ({
  gueltig_ab: morgen(),
  grundpreis_cent: 38,
  ref_fett: 4.2,
  ref_eiweiss: 3.4,
  fett_cent_je_zehntel: 0.45,
  eiweiss_cent_je_zehntel: 0.65,
  // Fixe Zuschläge & Abzüge
  hemmstoff_abzug_cent: 5,
  hemmstoff_abzug_aktiv: true,
  gvo_bonus_cent: 0.5,
  gvo_bonus_aktiv: false,
  ama_beitrag_cent: 0.35,
  ama_beitrag_aktiv: false,
  ust_satz: 13,
  ust_satz_pauschaliert: 13,
  ust_satz_regelbesteuert: 10,
  qualitaetsklassen: [],
  mengenstaffel: [],
  regeln: [],
});

// ---------- Sektions-Header (einheitlich für den Editor) ----------
const SektionHeader = ({ icon: Icon, title, hint, action }) => (
  <div className="flex items-start justify-between gap-4 mb-3">
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h4 className="font-semibold leading-tight">{title}</h4>
        {hint ? (
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        ) : null}
      </div>
    </div>
    {action}
  </div>
);

// ---------- Revisions-Editor ----------
const RevisionsEditor = ({ rev, milchsorteName, milchsorteId, onClose, onSaved }) => {
  // Legacy-Revisionen ohne getrennte USt-Sätze: mit Fallback aus `ust_satz` befüllen
  const initial = rev
    ? {
        ...rev,
        ust_satz_pauschaliert:
          rev.ust_satz_pauschaliert ?? rev.ust_satz ?? 13,
        ust_satz_regelbesteuert:
          rev.ust_satz_regelbesteuert ?? 10,
        // Fixe-Aktivierungen: Default true nur für Hemmstoff (Legacy-Verhalten)
        hemmstoff_abzug_aktiv:
          rev.hemmstoff_abzug_aktiv ?? true,
        gvo_bonus_aktiv: rev.gvo_bonus_aktiv ?? false,
        ama_beitrag_aktiv: rev.ama_beitrag_aktiv ?? false,
        gvo_bonus_cent: rev.gvo_bonus_cent ?? 0.5,
        ama_beitrag_cent: rev.ama_beitrag_cent ?? 0.35,
      }
    : leereRevision();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const isNew = !rev?.id;
  const editSperr = rev?.editierbar === false;
  const readOnly = editSperr;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Qualitätsklassen
  const setKlasse = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      qualitaetsklassen: f.qualitaetsklassen.map((x, i) =>
        i === idx ? { ...x, [k]: v } : x
      ),
    }));
  const addKlasse = () =>
    setForm((f) => ({
      ...f,
      qualitaetsklassen: [...f.qualitaetsklassen, leereKlasse()],
    }));
  const delKlasse = (idx) =>
    setForm((f) => ({
      ...f,
      qualitaetsklassen: f.qualitaetsklassen.filter((_, i) => i !== idx),
    }));

  // Lädt die vom Backend hinterlegten Standardklassen (S/1/2) in die
  // aktuelle Revision. Ersetzt bestehende Klassen nur nach Bestätigung.
  const [standardKlassenLoading, setStandardKlassenLoading] = useState(false);
  const loadStandardKlassen = async () => {
    const bereitsVorhanden = (form.qualitaetsklassen || []).length > 0;
    if (bereitsVorhanden) {
      const ok = window.confirm(
        `Es sind bereits ${form.qualitaetsklassen.length} Klasse(n) definiert.\n\n` +
        "Sollen die bestehenden Klassen durch die Standardklassen (S, 1, 2) ersetzt werden?",
      );
      if (!ok) return;
    }
    setStandardKlassenLoading(true);
    try {
      const res = await api.get("/preismodell/standardklassen");
      setForm((f) => ({ ...f, qualitaetsklassen: res.data }));
      toast.success(`${res.data.length} Standardklassen geladen. Werte prüfen und speichern.`);
    } catch (e) {
      toast.error(errMsg(e, "Standardklassen konnten nicht geladen werden."));
    } finally {
      setStandardKlassenLoading(false);
    }
  };

  // Mengenstaffel
  const setStaffel = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      mengenstaffel: (f.mengenstaffel || []).map((x, i) =>
        i === idx ? { ...x, [k]: v } : x
      ),
    }));
  const addStaffel = () =>
    setForm((f) => ({
      ...f,
      mengenstaffel: [...(f.mengenstaffel || []), leereStaffel()],
    }));
  const delStaffel = (idx) =>
    setForm((f) => ({
      ...f,
      mengenstaffel: (f.mengenstaffel || []).filter((_, i) => i !== idx),
    }));

  // Regeln
  const setRegel = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      regeln: f.regeln.map((x, i) => (i === idx ? { ...x, [k]: v } : x)),
    }));
  const addRegel = () =>
    setForm((f) => ({ ...f, regeln: [...f.regeln, leereRegel()] }));
  const delRegel = (idx) =>
    setForm((f) => ({
      ...f,
      regeln: f.regeln.filter((_, i) => i !== idx),
    }));
  const addBedingung = (idx) =>
    setForm((f) => ({
      ...f,
      regeln: f.regeln.map((r, i) =>
        i === idx
          ? {
              ...r,
              bedingungen: [
                ...r.bedingungen,
                { feld: "menge_kg", operator: ">=", wert: 0 },
              ],
            }
          : r
      ),
    }));
  const setBedingung = (rIdx, bIdx, k, v) =>
    setForm((f) => ({
      ...f,
      regeln: f.regeln.map((r, i) =>
        i === rIdx
          ? {
              ...r,
              bedingungen: r.bedingungen.map((b, bi) =>
                bi === bIdx ? { ...b, [k]: v } : b
              ),
            }
          : r
      ),
    }));
  const delBedingung = (rIdx, bIdx) =>
    setForm((f) => ({
      ...f,
      regeln: f.regeln.map((r, i) =>
        i === rIdx
          ? {
              ...r,
              bedingungen: r.bedingungen.filter((_, bi) => bi !== bIdx),
            }
          : r
      ),
    }));

  const sortedStaffel = useMemo(
    () =>
      [...(form.mengenstaffel || [])].map((s, i) => ({ ...s, _i: i })).sort(
        (a, b) => Number(a.ab_menge_kg || 0) - Number(b.ab_menge_kg || 0)
      ),
    [form.mengenstaffel]
  );
  const sortedKlassen = useMemo(
    () =>
      [...(form.qualitaetsklassen || [])]
        .map((k, i) => ({ ...k, _i: i }))
        .sort((a, b) => Number(a.prioritaet || 0) - Number(b.prioritaet || 0)),
    [form.qualitaetsklassen]
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        milchsorte_id: milchsorteId,
        gueltig_ab: form.gueltig_ab,
        grundpreis_cent: Number(form.grundpreis_cent),
        ref_fett: Number(form.ref_fett),
        ref_eiweiss: Number(form.ref_eiweiss),
        fett_cent_je_zehntel: Number(form.fett_cent_je_zehntel),
        eiweiss_cent_je_zehntel: Number(form.eiweiss_cent_je_zehntel),
        hemmstoff_abzug_cent: Number(form.hemmstoff_abzug_cent),
        hemmstoff_abzug_aktiv: !!form.hemmstoff_abzug_aktiv,
        gvo_bonus_cent: Number(form.gvo_bonus_cent || 0),
        gvo_bonus_aktiv: !!form.gvo_bonus_aktiv,
        ama_beitrag_cent: Number(form.ama_beitrag_cent || 0),
        ama_beitrag_aktiv: !!form.ama_beitrag_aktiv,
        ust_satz: Number(form.ust_satz),
        ust_satz_pauschaliert: Number(form.ust_satz_pauschaliert ?? form.ust_satz ?? 13),
        ust_satz_regelbesteuert: Number(form.ust_satz_regelbesteuert ?? 10),
        qualitaetsklassen: form.qualitaetsklassen.map((k) => ({
          ...k,
          prioritaet: Number(k.prioritaet),
          zellzahl_max: Number(k.zellzahl_max),
          keimzahl_max: Number(k.keimzahl_max),
          cent_je_kg: Number(k.cent_je_kg),
        })),
        mengenstaffel: (form.mengenstaffel || []).map((s) => ({
          ...s,
          ab_menge_kg: Number(s.ab_menge_kg),
          cent_je_kg: Number(s.cent_je_kg),
        })),
        regeln: form.regeln.map((r) => ({
          ...r,
          cent_je_kg: Number(r.cent_je_kg),
          bedingungen: (r.bedingungen || []).map((b) => ({
            ...b,
            wert:
              b.wert === "true"
                ? true
                : b.wert === "false"
                ? false
                : isNaN(Number(b.wert))
                ? b.wert
                : Number(b.wert),
          })),
        })),
      };
      if (isNew) {
        await api.post(`/preismodell/${milchsorteId}/revisionen`, payload);
        toast.success("Neue Revision angelegt.");
      } else {
        await api.put(`/preismodell/revisionen/${rev.id}`, payload);
        toast.success("Revision gespeichert.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="revision-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNew ? "Neue Revision anlegen" : "Revision bearbeiten"}
            {milchsorteName ? (
              <Badge variant="secondary" className="font-normal">
                {milchsorteName}
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {editSperr
              ? "Diese Revision ist gesperrt (Abrechnungen basieren darauf). Um sie zu ändern, legen Sie eine neue Revision mit späterem Stichtag an oder stornieren Sie die betroffenen Abrechnungen."
              : "Der Editor ist in vier klare Blöcke gegliedert: Basisparameter, Mengenstaffel, Qualitätsklassen und dynamische Regeln."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* ============================== 1) BASISPARAMETER ============================== */}
          <Card data-testid="section-basisparameter">
            <CardContent className="pt-5">
              <SektionHeader
                icon={Calculator}
                title="Basisparameter"
                hint="Grundpreis, Referenzwerte und Standard-Zuschläge je Zehntelprozent Fett/Eiweiß."
              />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="col-span-2 lg:col-span-4 space-y-1.5">
                  <Label>Gültig ab (Stichtag)</Label>
                  <Input
                    type="date"
                    data-testid="revision-gueltig-ab"
                    value={form.gueltig_ab}
                    disabled={readOnly || !isNew}
                    onChange={(e) => setField("gueltig_ab", e.target.value)}
                    className="max-w-[240px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Neue Revision beginnt ab diesem Datum. Die bisherige Revision wird automatisch zum Vortag geschlossen.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Grundpreis (ct/kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    data-testid="revision-grundpreis"
                    value={form.grundpreis_cent}
                    disabled={readOnly}
                    onChange={(e) =>
                      setField("grundpreis_cent", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>USt Pauschalierung (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    data-testid="revision-ust-pauschal"
                    value={form.ust_satz_pauschaliert ?? ""}
                    disabled={readOnly}
                    onChange={(e) => setField("ust_satz_pauschaliert", e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Bauern-Pauschalierung (Ö: 13 %)
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>USt Regelbesteuerung (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    data-testid="revision-ust-regel"
                    value={form.ust_satz_regelbesteuert ?? ""}
                    disabled={readOnly}
                    onChange={(e) => setField("ust_satz_regelbesteuert", e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Buchhaltungspflichtig (Ö: 10 %)
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <Label>Referenz Fett (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.ref_fett}
                    disabled={readOnly}
                    onChange={(e) => setField("ref_fett", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Fett ct/Zehntel</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.fett_cent_je_zehntel}
                    disabled={readOnly}
                    onChange={(e) =>
                      setField("fett_cent_je_zehntel", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Referenz Eiweiß (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.ref_eiweiss}
                    disabled={readOnly}
                    onChange={(e) => setField("ref_eiweiss", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Eiweiß ct/Zehntel</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.eiweiss_cent_je_zehntel}
                    disabled={readOnly}
                    onChange={(e) =>
                      setField("eiweiss_cent_je_zehntel", e.target.value)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ============================== 2) FIXE ZUSCHLÄGE & ABZÜGE ============================== */}
          <Card data-testid="section-fixe-zuschlaege">
            <CardContent className="pt-5 space-y-4">
              <SektionHeader
                icon={Coins}
                title="Fixe Zuschläge & Abzüge"
                hint="Pauschale Beträge, die je Kilogramm angewendet werden. Jede Position kann einzeln aktiviert oder deaktiviert werden."
              />

              {/* Hemmstoff-Abzug */}
              <div className="rounded-lg border p-3 space-y-2" data-testid="fix-hemmstoff-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid="fix-hemmstoff-switch"
                        checked={!!form.hemmstoff_abzug_aktiv}
                        disabled={readOnly}
                        onCheckedChange={(v) => setField("hemmstoff_abzug_aktiv", v)}
                      />
                      <Label className="font-medium cursor-pointer" onClick={() =>
                        !readOnly && setField("hemmstoff_abzug_aktiv", !form.hemmstoff_abzug_aktiv)}>
                        Hemmstoff-Abzug
                      </Label>
                      <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px] py-0 h-4">
                        Abzug
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Wird nur bei positivem Hemmstoff-Befund abgezogen (auf Basis der Milchproben).
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      step="0.1"
                      value={form.hemmstoff_abzug_cent}
                      disabled={readOnly || !form.hemmstoff_abzug_aktiv}
                      data-testid="fix-hemmstoff-cent"
                      className="w-24 font-num"
                      onChange={(e) => setField("hemmstoff_abzug_cent", e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground w-14 shrink-0">ct / kg</span>
                  </div>
                </div>
              </div>

              {/* GVO-frei-Bonus */}
              <div className="rounded-lg border p-3 space-y-2" data-testid="fix-gvo-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid="fix-gvo-switch"
                        checked={!!form.gvo_bonus_aktiv}
                        disabled={readOnly}
                        onCheckedChange={(v) => setField("gvo_bonus_aktiv", v)}
                      />
                      <Label className="font-medium cursor-pointer" onClick={() =>
                        !readOnly && setField("gvo_bonus_aktiv", !form.gvo_bonus_aktiv)}>
                        GVO-frei-Bonus
                      </Label>
                      <Badge variant="outline" className="border-[hsl(var(--success))] text-[hsl(var(--success))] text-[10px] py-0 h-4">
                        Zuschlag
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Zuschlag für gentechnikfreie Fütterung. Wirkt nur, wenn die Milchsorte als
                      <span className="font-medium"> &bdquo;GVO-frei&ldquo;</span> markiert ist.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      step="0.1"
                      value={form.gvo_bonus_cent}
                      disabled={readOnly || !form.gvo_bonus_aktiv}
                      data-testid="fix-gvo-cent"
                      className="w-24 font-num"
                      onChange={(e) => setField("gvo_bonus_cent", e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground w-14 shrink-0">ct / kg</span>
                  </div>
                </div>
              </div>

              {/* AMA-Beitrag */}
              <div className="rounded-lg border p-3 space-y-2" data-testid="fix-ama-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid="fix-ama-switch"
                        checked={!!form.ama_beitrag_aktiv}
                        disabled={readOnly}
                        onCheckedChange={(v) => setField("ama_beitrag_aktiv", v)}
                      />
                      <Label className="font-medium cursor-pointer" onClick={() =>
                        !readOnly && setField("ama_beitrag_aktiv", !form.ama_beitrag_aktiv)}>
                        AMA-Beitrag
                      </Label>
                      <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px] py-0 h-4">
                        Abzug
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pauschaler Beitrag an die Agrarmarkt Austria (Vermarktungsbeitrag). Wird auf alle Lieferungen angewendet.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      step="0.01"
                      value={form.ama_beitrag_cent}
                      disabled={readOnly || !form.ama_beitrag_aktiv}
                      data-testid="fix-ama-cent"
                      className="w-24 font-num"
                      onChange={(e) => setField("ama_beitrag_cent", e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground w-14 shrink-0">ct / kg</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ============================== 2) MENGENSTAFFEL ============================== */}
          <Card data-testid="section-mengenstaffel">
            <CardContent className="pt-5">
              <SektionHeader
                icon={Layers}
                title="Mengenstaffel"
                hint="Zuschlag ab einer bestimmten Monatsmenge (in kg). Jede erreichte Stufe wird kumulativ angerechnet."
                action={
                  !readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addStaffel}
                      data-testid="add-mengenstaffel"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Staffel
                    </Button>
                  )
                }
              />

              {sortedStaffel.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Keine Mengenstaffel definiert. Über &bdquo;Staffel&ldquo; hinzufügen.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Ab Menge (kg)</TableHead>
                        <TableHead className="w-[140px]">Zuschlag (ct/kg)</TableHead>
                        <TableHead>Beschreibung</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedStaffel.map((s) => (
                        <TableRow key={s._i} data-testid={`mengenstaffel-row-${s._i}`}>
                          <TableCell>
                            <Input
                              type="number"
                              step="100"
                              value={s.ab_menge_kg}
                              disabled={readOnly}
                              onChange={(e) =>
                                setStaffel(s._i, "ab_menge_kg", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.1"
                              value={s.cent_je_kg}
                              disabled={readOnly}
                              onChange={(e) =>
                                setStaffel(s._i, "cent_je_kg", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={s.beschreibung || ""}
                              placeholder="z. B. Großmengen-Bonus"
                              disabled={readOnly}
                              onChange={(e) =>
                                setStaffel(s._i, "beschreibung", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => delStaffel(s._i)}
                              disabled={readOnly}
                              data-testid={`del-mengenstaffel-${s._i}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ============================== 3) QUALITÄTSKLASSEN ============================== */}
          <Card data-testid="section-qualitaetsklassen">
            <CardContent className="pt-5">
              <SektionHeader
                icon={Award}
                title="Qualitätsklassen"
                hint="Niedrige Priorität = bessere Klasse. Die erste passende Klasse gewinnt."
                action={
                  !readOnly && (
                    <div className="flex gap-1.5 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={loadStandardKlassen}
                        disabled={standardKlassenLoading}
                        data-testid="load-standard-klassen"
                        title="Lädt die vom System hinterlegten Standardklassen (S, 1, 2) als Ausgangspunkt in diese Revision"
                      >
                        {standardKlassenLoading
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                        Standard laden
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={addKlasse}
                        data-testid="add-quality-class"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Klasse
                      </Button>
                    </div>
                  )
                }
              />

              {sortedKlassen.length === 0 ? (
                <div className="rounded-md border border-dashed border-amber-400/50 bg-amber-50/50 dark:bg-amber-950/20 p-6 text-center space-y-2" data-testid="klassen-empty-warning">
                  <div className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    <p className="text-sm font-medium">Keine Qualitätsklassen definiert</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Abrechnungen mit dieser Revision zeigen keine Klasse an und verrechnen keine
                    klassenbezogenen Zu-/Abschläge. Falls das gewollt ist, kann diese Warnung ignoriert werden.
                  </p>
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={loadStandardKlassen}
                      disabled={standardKlassenLoading}
                      data-testid="load-standard-klassen-empty"
                    >
                      {standardKlassenLoading
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                      Standardklassen laden
                    </Button>
                  )}
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[70px]">Prio</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="w-[140px]">Vorschau</TableHead>
                        <TableHead className="w-[110px]">Icon</TableHead>
                        <TableHead className="w-[110px]">Farbe</TableHead>
                        <TableHead className="w-[130px]">Zellzahl max</TableHead>
                        <TableHead className="w-[130px]">Keimzahl max</TableHead>
                        <TableHead className="w-[120px]">Zu-/Abschlag (ct/kg)</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedKlassen.map((k) => (
                        <TableRow key={k._i} data-testid={`qualitaet-row-${k._i}`}>
                          <TableCell>
                            <Input
                              type="number"
                              value={k.prioritaet}
                              disabled={readOnly}
                              onChange={(e) =>
                                setKlasse(k._i, "prioritaet", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={k.name}
                              placeholder="z. B. S, I, II"
                              disabled={readOnly}
                              onChange={(e) =>
                                setKlasse(k._i, "name", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <QualityBadge
                              klasse={k.name || "?"}
                              icon={k.icon}
                              farbe={k.farbe}
                              title={k.beschreibung || k.name}
                            />
                          </TableCell>
                          <TableCell>
                            <IconPicker
                              value={k.icon || "BadgeCheck"}
                              disabled={readOnly}
                              onChange={(v) => setKlasse(k._i, "icon", v)}
                              testId={`quality-icon-${k._i}`}
                            />
                          </TableCell>
                          <TableCell>
                            <ColorPicker
                              value={k.farbe || "info"}
                              disabled={readOnly}
                              onChange={(v) => setKlasse(k._i, "farbe", v)}
                              testId={`quality-color-${k._i}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={k.zellzahl_max}
                              disabled={readOnly}
                              onChange={(e) =>
                                setKlasse(k._i, "zellzahl_max", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={k.keimzahl_max}
                              disabled={readOnly}
                              onChange={(e) =>
                                setKlasse(k._i, "keimzahl_max", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.1"
                              value={k.cent_je_kg}
                              disabled={readOnly}
                              onChange={(e) =>
                                setKlasse(k._i, "cent_je_kg", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => delKlasse(k._i)}
                              disabled={readOnly}
                              data-testid={`del-quality-class-${k._i}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ============================== 4) DYNAMISCHE REGELN ============================== */}
          <Card data-testid="section-regeln">
            <CardContent className="pt-5">
              <SektionHeader
                icon={Settings2}
                title="Dynamische Zuschläge & Abzüge"
                hint="Beträge in ct/kg (negativ = Abzug). Ohne Bedingung gilt die Regel immer."
                action={
                  !readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addRegel}
                      data-testid="add-rule"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Regel
                    </Button>
                  )
                }
              />

              {form.regeln.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Keine dynamischen Regeln definiert.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {form.regeln.map((r, i) => (
                    <div
                      key={i}
                      className="rounded-lg border p-3 space-y-3 bg-card"
                      data-testid={`rule-${i}`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-6 space-y-1">
                          <Label className="text-xs">Regelname</Label>
                          <Input
                            value={r.name}
                            disabled={readOnly}
                            onChange={(e) =>
                              setRegel(i, "name", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">Betrag (ct/kg)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={r.cent_je_kg}
                            disabled={readOnly}
                            onChange={(e) =>
                              setRegel(i, "cent_je_kg", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-span-2 flex items-end gap-2 h-full pb-1">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={r.aktiv}
                              disabled={readOnly}
                              onCheckedChange={(v) =>
                                setRegel(i, "aktiv", v)
                              }
                            />
                            <span className="text-xs">Aktiv</span>
                          </div>
                        </div>
                        <div className="col-span-1 flex items-end justify-end pb-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => delRegel(i)}
                            disabled={readOnly}
                            data-testid={`del-rule-${i}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      <Textarea
                        rows={1}
                        placeholder="Beschreibung (optional)"
                        value={r.beschreibung || ""}
                        disabled={readOnly}
                        onChange={(e) =>
                          setRegel(i, "beschreibung", e.target.value)
                        }
                      />

                      <div className="rounded-md bg-muted/40 p-3 space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium">Bedingungen</span>
                          <span>(Verknüpfung:</span>
                          <Select
                            value={r.verknuepfung}
                            disabled={readOnly}
                            onValueChange={(v) =>
                              setRegel(i, "verknuepfung", v)
                            }
                          >
                            <SelectTrigger className="w-20 h-7">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="und">UND</SelectItem>
                              <SelectItem value="oder">ODER</SelectItem>
                            </SelectContent>
                          </Select>
                          <span>)</span>
                          {!readOnly && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addBedingung(i)}
                              className="ml-auto h-6"
                              data-testid={`add-condition-${i}`}
                            >
                              <Plus className="h-3 w-3 mr-1" /> Bedingung
                            </Button>
                          )}
                        </div>
                        {(r.bedingungen || []).map((b, bi) => (
                          <div
                            key={bi}
                            className="grid grid-cols-12 gap-2 items-center"
                          >
                            <Select
                              value={b.feld}
                              disabled={readOnly}
                              onValueChange={(v) =>
                                setBedingung(i, bi, "feld", v)
                              }
                            >
                              <SelectTrigger className="col-span-5 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FELDER.map((f) => (
                                  <SelectItem key={f.key} value={f.key}>
                                    {f.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={b.operator}
                              disabled={readOnly}
                              onValueChange={(v) =>
                                setBedingung(i, bi, "operator", v)
                              }
                            >
                              <SelectTrigger className="col-span-2 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {OPERATOREN.map((o) => (
                                  <SelectItem key={o.key} value={o.key}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              className="col-span-4 h-8"
                              value={String(b.wert ?? "")}
                              disabled={readOnly}
                              onChange={(e) =>
                                setBedingung(i, bi, "wert", e.target.value)
                              }
                              placeholder="Wert (true/false für Ja/Nein)"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="col-span-1"
                              onClick={() => delBedingung(i, bi)}
                              disabled={readOnly}
                              data-testid={`del-condition-${i}-${bi}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                        {(!r.bedingungen || r.bedingungen.length === 0) && (
                          <p className="text-xs text-muted-foreground italic">
                            Keine Bedingungen – Regel gilt immer.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={save}
            disabled={saving || readOnly}
            data-testid="revision-save-button"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isNew ? "Revision anlegen" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Revisionshistorie ----------
// ---------- Diff-Helfer für Zeitstrahl-Ansicht ----------
const NUM_LABELS = {
  grundpreis_cent: { name: "Grundpreis", einheit: "ct/kg", dez: 2 },
  ref_fett: { name: "Ref. Fett", einheit: "%", dez: 2 },
  ref_eiweiss: { name: "Ref. Eiweiß", einheit: "%", dez: 2 },
  fett_cent_je_zehntel: { name: "Fett-Zuschlag", einheit: "ct/0,1%", dez: 2 },
  eiweiss_cent_je_zehntel: { name: "Eiweiß-Zuschlag", einheit: "ct/0,1%", dez: 2 },
  hemmstoff_abzug_cent: { name: "Hemmstoff-Abzug", einheit: "ct/kg", dez: 2 },
  gvo_bonus_cent: { name: "GVO-frei-Bonus", einheit: "ct/kg", dez: 2 },
  ama_beitrag_cent: { name: "AMA-Beitrag", einheit: "ct/kg", dez: 2 },
  ust_satz_pauschaliert: { name: "USt Pauschalierung", einheit: "%", dez: 1 },
  ust_satz_regelbesteuert: { name: "USt Regelbesteuerung", einheit: "%", dez: 1 },
};

const BOOL_LABELS = {
  hemmstoff_abzug_aktiv: "Hemmstoff-Abzug",
  gvo_bonus_aktiv: "GVO-frei-Bonus",
  ama_beitrag_aktiv: "AMA-Beitrag",
};

const berechneDiff = (alt, neu) => {
  const diffs = [];
  // Numerische Änderungen
  for (const [key, meta] of Object.entries(NUM_LABELS)) {
    const av = Number(alt?.[key] ?? 0);
    const nv = Number(neu?.[key] ?? 0);
    if (!alt || Math.abs(av - nv) > 0.0001) {
      diffs.push({
        typ: "num", key, name: meta.name, einheit: meta.einheit, dez: meta.dez,
        alt: av, neu: nv, delta: nv - av,
      });
    }
  }
  // Boolean-Toggles (Aktivierungen)
  for (const [key, name] of Object.entries(BOOL_LABELS)) {
    const av = !!alt?.[key];
    const nv = !!neu?.[key];
    if (alt && av !== nv) {
      diffs.push({ typ: "bool", key, name, alt: av, neu: nv });
    }
  }
  // Anzahl-Vergleiche (Klassen / Staffeln / Regeln)
  const arrKeys = [
    { k: "qualitaetsklassen", n: "Qualitätsklassen" },
    { k: "mengenstaffel", n: "Mengenstaffel-Stufen" },
    { k: "regeln", n: "Dynamische Regeln" },
  ];
  for (const { k, n } of arrKeys) {
    const av = (alt?.[k] || []).length;
    const nv = (neu?.[k] || []).length;
    if (alt && av !== nv) {
      diffs.push({ typ: "count", key: k, name: n, alt: av, neu: nv });
    }
  }
  return diffs;
};

const ZeitstrahlAnsicht = ({ chrono, onEdit, onDelete }) => {
  if (chrono.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        Noch keine Revisionen vorhanden.
      </p>
    );
  }
  // chrono ist bereits chronologisch aufsteigend (älteste zuerst) – wichtig für Diff-Berechnung.
  // Für Anzeige: absteigend (neueste oben) sortieren.
  const heute = new Date().toISOString().slice(0, 10);
  // Aktive Revision = jene mit größtem gueltig_ab ≤ heute (und keine neuere existiert).
  // Da chrono aufsteigend sortiert ist, ist es die letzte Revision mit gueltig_ab ≤ heute.
  let aktiveRevId = null;
  for (let i = chrono.length - 1; i >= 0; i--) {
    if ((chrono[i].gueltig_ab || "") <= heute) {
      aktiveRevId = chrono[i].id;
      break;
    }
  }
  const displayOrder = [...chrono].reverse(); // neueste zuerst

  return (
    <div className="relative pl-6 space-y-6" data-testid="revision-timeline">
      {/* Vertikale Zeitachsen-Linie */}
      <div className="absolute left-2 top-2 bottom-2 w-px bg-border" />
      {displayOrder.map((rev) => {
        // Chronologischer Index (in aufsteigender Reihenfolge) für Diff gegenüber Vorgänger
        const chronoIdx = chrono.findIndex((c) => c.id === rev.id);
        const vorher = chronoIdx > 0 ? chrono[chronoIdx - 1] : null;
        const diffs = berechneDiff(vorher, rev);
        const istErsteRev = !vorher;
        const istAktiv = rev.id === aktiveRevId;
        const istGeplant = (rev.gueltig_ab || "") > heute;
        return (
          <div key={rev.id} className="relative" data-testid={`timeline-entry-${rev.id}`}>
            {/* Marker auf der Achse */}
            <div className="absolute -left-[18px] top-1 flex h-4 w-4 items-center justify-center">
              <div className={`h-3 w-3 rounded-full border-2 ${
                istAktiv
                  ? "border-primary bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]"
                  : istGeplant
                  ? "border-amber-500 bg-amber-500/20"
                  : "border-muted-foreground bg-background"
              }`} />
            </div>

            <div className="flex flex-wrap items-baseline gap-2 mb-2">
              <span className="font-num text-sm font-medium">{rev.gueltig_ab}</span>
              <span className="text-xs text-muted-foreground">
                {rev.gueltig_bis ? `bis ${rev.gueltig_bis}` : istGeplant ? "geplant" : "aktuell gültig"}
              </span>
              {istAktiv && (
                <Badge className="text-[10px] py-0 h-4 bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`badge-aktiv-${rev.id}`}>
                  Aktiv
                </Badge>
              )}
              {istGeplant && (
                <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-500 text-amber-600 dark:text-amber-400" data-testid={`badge-geplant-${rev.id}`}>
                  Geplant
                </Badge>
              )}
              {rev.editierbar ? (
                <Badge variant="secondary" className="text-[10px] py-0 h-4">editierbar</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] py-0 h-4">
                  <Lock className="h-2.5 w-2.5 mr-1" />
                  gesperrt · {rev.verwendet_in_abrechnungen} Abr.
                </Badge>
              )}
              <div className="ml-auto flex gap-0.5">
                <Button
                  variant="ghost" size="sm" className="h-7"
                  onClick={() => onEdit(rev)}
                  data-testid={`timeline-edit-${rev.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Details
                </Button>
                {onDelete && rev.editierbar && (
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => onDelete(rev.id)}
                    data-testid={`timeline-delete-${rev.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </div>

            {istErsteRev ? (
              <div className="rounded-md border bg-secondary/40 p-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5" />
                  <span className="font-medium text-foreground">Erste Revision – Ausgangswerte:</span>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 font-num tabular-nums">
                  <span>Grundpreis: {fmtNum(rev.grundpreis_cent, 2)} ct</span>
                  <span>Fett-Ref: {fmtNum(rev.ref_fett, 2)}%</span>
                  <span>Eiweiß-Ref: {fmtNum(rev.ref_eiweiss, 2)}%</span>
                  <span>USt pauschal: {fmtNum(rev.ust_satz_pauschaliert ?? rev.ust_satz ?? 0, 1)}%</span>
                  <span>USt regel: {fmtNum(rev.ust_satz_regelbesteuert ?? rev.ust_satz ?? 0, 1)}%</span>
                  <span>Regeln: {(rev.regeln || []).length}</span>
                </div>
              </div>
            ) : diffs.length === 0 ? (
              <div className="rounded-md border p-3 text-xs text-muted-foreground italic">
                Keine wesentlichen Änderungen zur vorherigen Revision.
              </div>
            ) : (
              <div className="rounded-md border p-3 space-y-1.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  {diffs.length} Änderung{diffs.length !== 1 ? "en" : ""}
                </p>
                {diffs.map((d, i) => (
                  <DiffZeile key={`${rev.id}-${i}`} d={d} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DiffZeile = ({ d }) => {
  if (d.typ === "num") {
    const stieg = d.delta > 0;
    const gefallen = d.delta < 0;
    const Icon = stieg ? TrendingUp : gefallen ? TrendingDown : Minus;
    const farbe = stieg
      ? "text-[hsl(var(--success))]"
      : gefallen
      ? "text-destructive"
      : "text-muted-foreground";
    return (
      <div className="flex items-center gap-2 text-xs">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${farbe}`} />
        <span className="font-medium min-w-[130px]">{d.name}:</span>
        <span className="font-num tabular-nums text-muted-foreground line-through">
          {fmtNum(d.alt, d.dez)} {d.einheit}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="font-num tabular-nums font-medium">
          {fmtNum(d.neu, d.dez)} {d.einheit}
        </span>
        <span className={`font-num tabular-nums ml-auto text-[11px] ${farbe}`}>
          {d.delta > 0 ? "+" : ""}{fmtNum(d.delta, d.dez)}
        </span>
      </div>
    );
  }
  if (d.typ === "bool") {
    return (
      <div className="flex items-center gap-2 text-xs">
        <div className={`h-3.5 w-3.5 rounded-full shrink-0 ${d.neu ? "bg-[hsl(var(--success))]" : "bg-muted-foreground"}`} />
        <span className="font-medium">{d.name}:</span>
        <Badge variant="outline" className="text-[10px] py-0 h-4">
          {d.neu ? "aktiviert" : "deaktiviert"}
        </Badge>
      </div>
    );
  }
  if (d.typ === "count") {
    const delta = d.neu - d.alt;
    return (
      <div className="flex items-center gap-2 text-xs">
        <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="font-medium min-w-[130px]">{d.name}:</span>
        <span className="font-num tabular-nums text-muted-foreground">{d.alt}</span>
        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="font-num tabular-nums font-medium">{d.neu}</span>
        <span className={`font-num tabular-nums ml-auto text-[11px] ${delta > 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
          {delta > 0 ? "+" : ""}{delta}
        </span>
      </div>
    );
  }
  return null;
};

const RevisionsHistorie = ({ milchsorteId, onEdit, onClose, onRefresh }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/preismodell/${milchsorteId}/revisionen`)
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [milchsorteId]);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Revision wirklich löschen?")) return;
    try {
      await api.delete(`/preismodell/revisionen/${id}`);
      toast.success("Revision gelöscht.");
      load();
      onRefresh?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Sortierung: älteste zuerst (chronologisch für Zeitstrahl)
  const chrono = [...rows].sort((a, b) =>
    (a.gueltig_ab || "").localeCompare(b.gueltig_ab || "")
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Revisions-Zeitstrahl
          </DialogTitle>
          <DialogDescription>
            Chronologische Übersicht aller Preismodell-Revisionen dieser Milchsorte inklusive der jeweiligen Änderungen.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" />
        ) : (
          <ZeitstrahlAnsicht chrono={chrono} onEdit={onEdit} onDelete={del} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Hauptseite ----------
export default function AdminPreismodellPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRev, setEditRev] = useState(null);
  const [editMilchsorte, setEditMilchsorte] = useState(null);
  const [editMilchsorteName, setEditMilchsorteName] = useState("");
  const [historieMs, setHistorieMs] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/preismodell")
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (msId, msName, rev) => {
    setEditMilchsorte(msId);
    setEditMilchsorteName(msName);
    setEditRev(rev);
  };

  const closeEditor = () => {
    setEditRev(null);
    setEditMilchsorte(null);
    setEditMilchsorteName("");
  };

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Preismodelle</h1>
        <p className="text-sm text-muted-foreground">
          Jede Milchsorte hat ein eigenes, revisioniertes Preismodell mit Basisparametern, Mengenstaffel, Qualitätsklassen und dynamischen Regeln.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Aktive Preismodelle je Milchsorte</CardTitle>
          <CardDescription>
            Übersicht der aktuell gültigen Revisionen. Über &bdquo;Historie&ldquo; gelangen Sie zu allen Revisionen einer Milchsorte.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="pricing-models-table">
            <TableHeader>
              <TableRow>
                <TableHead>Milchsorte</TableHead>
                <TableHead>Aktive Revision</TableHead>
                <TableHead className="text-right">Grundpreis ct/kg</TableHead>
                <TableHead>Umfang</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => {
                const r = e.aktive_revision;
                return (
                  <TableRow key={e.milchsorte_id}>
                    <TableCell>
                      <div className="font-medium">{e.milchsorte_name}</div>
                      <div className="text-xs text-muted-foreground">
                        Code {e.milchsorte_code}
                      </div>
                    </TableCell>
                    <TableCell>
                      {r ? (
                        <span className="font-num">gültig ab {r.gueltig_ab}</span>
                      ) : (
                        <span className="text-muted-foreground italic">
                          Keine aktive Revision
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-num font-medium">
                      {r ? fmtNum(r.grundpreis_cent, 2) : "–"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r
                        ? `${r.mengenstaffel?.length || 0} Staffeln · ${
                            r.qualitaetsklassen?.length || 0
                          } Klassen · ${r.regeln?.length || 0} Regeln`
                        : "–"}
                    </TableCell>
                    <TableCell>
                      {r ? (
                        r.editierbar ? (
                          <Badge variant="secondary">editierbar</Badge>
                        ) : (
                          <Badge variant="outline">
                            <Lock className="h-3 w-3 mr-1" />
                            gesperrt ({r.verwendet_in_abrechnungen} Abr.)
                          </Badge>
                        )
                      ) : (
                        "–"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setHistorieMs(e.milchsorte_id)}
                        >
                          <History className="h-4 w-4 mr-1" /> Historie ({e.anzahl_revisionen})
                        </Button>
                        {r && (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`edit-active-${e.milchsorte_code}`}
                            onClick={() =>
                              openEditor(e.milchsorte_id, e.milchsorte_name, r)
                            }
                          >
                            <Pencil className="h-4 w-4 mr-1" /> Aktuell gültig
                          </Button>
                        )}
                        <Button
                          size="sm"
                          data-testid={`new-revision-${e.milchsorte_code}`}
                          onClick={() =>
                            openEditor(e.milchsorte_id, e.milchsorte_name, null)
                          }
                        >
                          <GitBranch className="h-4 w-4 mr-1" /> Neue Revision
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editMilchsorte && (
        <RevisionsEditor
          rev={editRev}
          milchsorteId={editMilchsorte}
          milchsorteName={editMilchsorteName}
          onClose={closeEditor}
          onSaved={load}
        />
      )}

      {historieMs && (
        <RevisionsHistorie
          milchsorteId={historieMs}
          onEdit={(r) => {
            const ms = rows.find((x) => x.milchsorte_id === historieMs);
            setHistorieMs(null);
            openEditor(historieMs, ms?.milchsorte_name || "", r);
          }}
          onClose={() => setHistorieMs(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
